import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../ui/card.js';
import { cn } from '../../lib/utils.js';
import { CheckCircle, Clock, AlertCircle, XCircle } from 'lucide-react';

interface SupportCardProps {
  variant?: 'default' | 'glass' | 'metric' | 'ticket' | 'article';
  children: React.ReactNode;
  className?: string;
  title?: string;
  description?: string;
  footer?: React.ReactNode;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  status?: 'open' | 'in-progress' | 'resolved' | 'closed';
  delay?: number;
  interactive?: boolean;
}

export const SupportCard: React.FC<SupportCardProps> = ({ 
  variant = 'default', 
  children, 
  className,
  title,
  description,
  footer,
  priority,
  status,
  delay = 0,
  interactive = true
}) => {
  const variantStyles = {
    default: 'violett-card',
    glass: 'violett-card',
    metric: 'violett-card border-[#4267A1]/30',
    ticket: 'violett-card border-yellow-500/30 hover:shadow-2xl hover:shadow-yellow-500/10',
    article: 'violett-card hover:shadow-2xl hover:shadow-[#4267A1]/10'
  };

  const priorityBorder = {
    low: 'border-l-4 border-l-green-400 shadow-lg shadow-green-400/20',
    medium: 'border-l-4 border-l-yellow-400 shadow-lg shadow-yellow-400/20', 
    high: 'border-l-4 border-l-orange-400 shadow-lg shadow-orange-400/20',
    urgent: 'border-l-4 border-l-red-500 shadow-lg shadow-red-500/20'
  };

  const statusConfig = {
    open: { 
      bg: 'bg-[#4267A1]/20 border-[#4267A1]/30', 
      text: 'text-[#4267A1]',
      icon: Clock,
      pulse: 'animate-pulse'
    },
    'in-progress': { 
      bg: 'bg-yellow-500/20 border-yellow-500/30', 
      text: 'text-yellow-300',
      icon: AlertCircle,
      pulse: 'animate-pulse'
    },
    resolved: { 
      bg: 'bg-green-500/20 border-green-500/30', 
      text: 'text-green-300',
      icon: CheckCircle,
      pulse: ''
    },
    closed: { 
      bg: 'bg-slate-500/20 border-slate-500/30', 
      text: 'text-slate-300',
      icon: XCircle,
      pulse: ''
    }
  };
  
  const statusInfo = status ? statusConfig[status] : null;
  const StatusIcon = statusInfo?.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      className={className}
    >
      <Card className={`${variantStyles[variant]} ${priority ? priorityBorder[priority] : ''} relative overflow-hidden group ${interactive ? 'cursor-pointer hover:scale-[1.02] transition-transform duration-300' : ''}`}>
        {status && statusInfo && (
          <motion.div 
            className={`absolute top-4 right-4 px-3 py-1.5 rounded-full text-xs font-medium border backdrop-blur-sm flex items-center gap-2 ${statusInfo.bg} ${statusInfo.text} ${statusInfo.pulse}`}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.3, delay: delay + 0.2 }}
          >
            {StatusIcon && <StatusIcon size={12} />}
            {status.replace('-', ' ')}
          </motion.div>
        )}
        
        {title && (
          <CardHeader className="pb-3">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: delay + 0.1 }}
            >
              <CardTitle className="text-lg font-semibold text-white group-hover:text-gradient transition-all duration-300">
                {title}
              </CardTitle>
              {description && (
                <CardDescription className="text-sm text-slate-300 mt-2 group-hover:text-slate-200 transition-colors duration-300">
                  {description}
                </CardDescription>
              )}
            </motion.div>
          </CardHeader>
        )}
        
        <CardContent className="space-y-4 text-white">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: delay + 0.2 }}
          >
            {children}
          </motion.div>
        </CardContent>
        
        {footer && (
          <CardFooter className="pt-4 border-t border-slate-700/50 bg-slate-800/30 group-hover:bg-slate-700/40 transition-colors duration-300">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: delay + 0.3 }}
              className="w-full"
            >
              {footer}
            </motion.div>
          </CardFooter>
        )}
        
        {/* Subtle glow effect on hover */}
        <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-violet-500/5 via-transparent to-cyan-500/5" />
        </div>
      </Card>
    </motion.div>
  );
};

export default SupportCard;
